import { FONTS } from '../../../config/app-config';
import { getResponseErrorMessage, getRequestErrorMessage } from '../../../utils/apiErrors';
import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    IconButton,
    Collapse,
    Snackbar,
    Alert,
    Button,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckIcon from '@mui/icons-material/Check';
import UndoIcon from '@mui/icons-material/Undo';
import LaunchIcon from '@mui/icons-material/Launch';
import { useAuth } from '../../../auth/AuthProvider';

import { secureFetch } from "../../../utils/secureFetch";

const FeedbackEvaluation = ({ open, onClose, adminApiBase }) => {
    const { isAuthenticated, login } = useAuth();

    const [rows, setRows] = useState([]);
    const [openRow, setOpenRow] = useState(null);           // ID der ausgeklappten Zeile
    const [snack, setSnack] = useState({ open: false, msg: '', undo: null, severity: 'success' });

    const notifyError = useCallback((error, fallback) => {
        setSnack({ open: true, msg: getRequestErrorMessage(error, fallback), undo: null, severity: 'error' });
    }, []);

    /* -------- Daten laden -------- */
    const load = useCallback(async () => {
        try {
            const res = await secureFetch(`${adminApiBase}user/feedback?done=false`, {}, true);
            if (!res.ok) throw new Error(await getResponseErrorMessage(res, 'Failed to load feedback.'));
            setRows(await res.json());
        } catch (err) {
            notifyError(err, 'Failed to load feedback.');
        }
    }, [adminApiBase, notifyError]);

    useEffect(() => {
        if (!open) return;
        if (!isAuthenticated) { login(); return; }
        load();
    }, [open, isAuthenticated, login, load]);

    /* -------- Done / Undo -------- */
    const patchDone = async (id, done) => {
        const response = await secureFetch(`${adminApiBase}user/feedback/${id}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ done }),
        }, true);
        if (!response.ok) throw new Error(await getResponseErrorMessage(response, 'Failed to update feedback.'));
    };

    const markDone = async (id) => {
        try {
            await patchDone(id, true);
            setRows((r) => r.filter((f) => f._id !== id));
            setSnack({ open: true, msg: 'Feedback marked done', undo: () => undoDone(id), severity: 'success' });
        } catch (err) {
            notifyError(err, 'Failed to mark feedback done.');
        }
    };

    const undoDone = async (id) => {
        try { await patchDone(id, false); load(); }
        catch (err) { notifyError(err, 'Failed to restore feedback.'); }
    };

    /* -------- Render -------- */
    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Feedback Evaluation
            </Typography>

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Feedback</TableCell>
                        <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Created</TableCell>
                        <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Subject</TableCell>
                        <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Reason</TableCell>
                        <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>User</TableCell>
                        <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Open Article</TableCell>
                        <TableCell sx={{ color: '#fff', fontFamily: FONTS.body }}>Done</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map((f) => (
                        <React.Fragment key={f._id}>
                            {/* Hauptzeile */}
                            <TableRow hover>
                                <TableCell>
                                    <IconButton
                                        size="small"
                                        onClick={() => setOpenRow(openRow === f._id ? null : f._id)}
                                    >
                                        {openRow === f._id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    </IconButton>
                                </TableCell>
                                <TableCell>{new Date(f.created).toLocaleString()}</TableCell>
                                <TableCell>{f.subject}</TableCell>
                                <TableCell>{f.reason}</TableCell>
                                <TableCell>{f.user_id}</TableCell>
                                <TableCell>
                                    <IconButton
                                        size="small"
                                        href={f.article_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <LaunchIcon fontSize="inherit" />
                                    </IconButton>
                                </TableCell>
                                <TableCell>
                                    <IconButton size="small" onClick={() => markDone(f._id)}>
                                        <CheckIcon fontSize="inherit" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>

                            {/* Detailzeile */}
                            <TableRow>
                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                                    <Collapse in={openRow === f._id} timeout="auto" unmountOnExit>
                                        <Typography sx={{ p: 2 }}>{f.text}</Typography>
                                    </Collapse>
                                </TableCell>
                            </TableRow>
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>

            <Snackbar
                open={snack.open}
                autoHideDuration={10000}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snack.severity} variant="filled"
                    onClose={() => setSnack((s) => ({ ...s, open: false }))}
                    action={snack.undo && (
                        <Button
                            startIcon={<UndoIcon />}
                            color="inherit"
                            size="small"
                            onClick={() => {
                                snack.undo?.();
                                setSnack((s) => ({ ...s, open: false }));
                            }}
                        >
                            UNDO
                        </Button>
                    )}
                >
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default FeedbackEvaluation;

